import { FormEvent, UIEvent, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

import {
    AdminUserEditCompanyOption,
    AdminUserRow,
    AdminUserUpdateResult,
} from '../../services/api';
import { useAdminUserEditOptions, useAdminUserMutations } from '../../hooks/useApi';
import { canUpdateAuth0RootProfileAttributes } from '../../lib/admin-user-auth0-syncability';
import { getMutationErrorMessage } from '../../lib/apiErrors';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
    formCheckboxListSurfaceClassName,
    formFieldGroupControlClassName,
    formFieldGroupSurfaceClassName,
} from '../ui/formPrimitives';
import { Input } from '../ui/input';

interface AdminEditUserModalProps {
    open: boolean;
    user: AdminUserRow | null;
    onClose: () => void;
    onSaved?: (result: AdminUserUpdateResult) => void;
}

function formatCompanyLabel(company: { companyId: number | null; companyName: string }): string {
    const name = company.companyName.trim() || 'Unnamed company';
    return company.companyId == null ? name : `${name} (#${company.companyId})`;
}

function formatTeamLabel(team: { teamId: number | null; teamName: string }): string {
    const name = team.teamName.trim() || 'Unnamed team';
    return team.teamId == null ? name : `${name} (#${team.teamId})`;
}

function userCompaniesToEditable(user: AdminUserRow): AdminUserEditCompanyOption[] {
    if (user.memberships.length === 0) {
        return [];
    }

    const companiesById = new Map<number, AdminUserEditCompanyOption>();
    for (const membership of user.memberships) {
        const existing = companiesById.get(membership.companyId);
        if (existing) {
            existing.teams.push({
                teamId: membership.teamId,
                teamName: membership.teamName,
            });
            continue;
        }
        companiesById.set(membership.companyId, {
            companyId: membership.companyId,
            companyName: membership.companyName,
            teams: [
                {
                    teamId: membership.teamId,
                    teamName: membership.teamName,
                },
            ],
        });
    }

    return [...companiesById.values()];
}

function getSingleMembershipCompanyId(user: AdminUserRow | null): number | null {
    if (!user || user.memberships.length === 0) {
        return null;
    }

    const companyIds = new Set(user.memberships.map((membership) => membership.companyId));
    if (companyIds.size !== 1) {
        return null;
    }

    return [...companyIds][0];
}

export function AdminEditUserModal({ open, user, onClose, onSaved }: AdminEditUserModalProps) {
    const { updateUser } = useAdminUserMutations();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isSso, setIsSso] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [companies, setCompanies] = useState<AdminUserEditCompanyOption[]>([]);
    const [teamIds, setTeamIds] = useState<number[]>([]);
    const [error, setError] = useState<string | null>(null);
    const currentUserCompanyId = getSingleMembershipCompanyId(user);
    const currentCompanyOptionsQuery = useAdminUserEditOptions(
        currentUserCompanyId == null ? undefined : String(currentUserCompanyId),
        25,
        open && currentUserCompanyId != null,
    );
    const currentCompanyOption =
        currentUserCompanyId == null
            ? null
            : (currentCompanyOptionsQuery.data?.pages
                  .flatMap((page) => page.items)
                  .find((company) => company.companyId === currentUserCompanyId) ?? null);

    useEffect(() => {
        if (!open || !user) return;
        setFirstName(user.firstName);
        setLastName(user.lastName);
        setIsSso(user.isSso);
        setIsActive(user.isActive);
        setCompanies(userCompaniesToEditable(user));
        setTeamIds(user.memberships.map((membership) => membership.teamId));
        setError(null);
    }, [open, user]);

    useEffect(() => {
        if (!open || !currentCompanyOption) return;

        setCompanies((current) => {
            if (current.length !== 1 || current[0].companyId !== currentCompanyOption.companyId) {
                return current;
            }

            return [currentCompanyOption];
        });
    }, [currentCompanyOption, open]);

    if (!open || !user) return null;

    const namesManagedByIdentityProvider = !canUpdateAuth0RootProfileAttributes(user.subjectId);

    const toggleTeam = (teamId: number) => {
        setTeamIds((current) =>
            current.includes(teamId)
                ? current.filter((candidate) => candidate !== teamId)
                : [...current, teamId],
        );
    };

    const addOrReplaceCompany = (nextCompany: AdminUserEditCompanyOption) => {
        setCompanies([nextCompany]);
        setTeamIds((current) =>
            current.filter((teamId) => nextCompany.teams.some((team) => team.teamId === teamId)),
        );
        setError(null);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);

        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();

        if (companies.length === 0) {
            setError('Choose at least one company.');
            return;
        }
        if (teamIds.length === 0) {
            setError('Choose at least one team.');
            return;
        }

        const selectedMemberships = companies.flatMap((company) =>
            company.teams
                .filter((team) => teamIds.includes(team.teamId))
                .map((team) => ({
                    companyId: company.companyId,
                    teamId: team.teamId,
                })),
        );
        if (selectedMemberships.length === 0) {
            setError('Choose at least one team.');
            return;
        }
        if (new Set(selectedMemberships.map((membership) => membership.companyId)).size > 1) {
            setError('Users can only belong to one company at a time.');
            return;
        }

        try {
            const result = await updateUser.mutateAsync({
                userId: user.id,
                request: {
                    firstName: trimmedFirstName,
                    lastName: trimmedLastName,
                    isSso,
                    isActive,
                    memberships: selectedMemberships,
                },
            });
            onSaved?.(result);
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Failed to update user.'));
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white shadow-xl">
                <div className="flex items-start justify-between gap-4 border-b p-5">
                    <div>
                        <h2 className="text-xl font-semibold text-neutral-950">Edit User</h2>
                        <p className="mt-1 text-sm text-neutral-500">
                            Update user attributes and team memberships. Email cannot be changed.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                    >
                        <X className="size-5" />
                    </button>
                </div>
                <form className="space-y-5 p-5" onSubmit={handleSubmit}>
                    {error && (
                        <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>
                    )}

                    <div className="grid gap-4 md:grid-cols-2">
                        <Field
                            label="First name"
                            hint={
                                namesManagedByIdentityProvider
                                    ? 'Managed by your identity provider'
                                    : undefined
                            }
                        >
                            <Input
                                value={firstName}
                                onChange={(event) => setFirstName(event.target.value)}
                                disabled={namesManagedByIdentityProvider}
                                className={
                                    namesManagedByIdentityProvider
                                        ? 'bg-neutral-200 text-neutral-500'
                                        : undefined
                                }
                            />
                        </Field>
                        <Field
                            label="Last name"
                            hint={
                                namesManagedByIdentityProvider
                                    ? 'Managed by your identity provider'
                                    : undefined
                            }
                        >
                            <Input
                                value={lastName}
                                onChange={(event) => setLastName(event.target.value)}
                                disabled={namesManagedByIdentityProvider}
                                className={
                                    namesManagedByIdentityProvider
                                        ? 'bg-neutral-200 text-neutral-500'
                                        : undefined
                                }
                            />
                        </Field>
                    </div>

                    <Field label="Email">
                        <Input
                            value={user.email}
                            disabled
                            className="bg-neutral-200 text-neutral-500"
                        />
                    </Field>

                    <div className="grid gap-3 md:grid-cols-2">
                        <Toggle
                            label="SSO"
                            checked={isSso}
                            onChange={setIsSso}
                            description="Marks this user as an SSO user in WEVO."
                        />
                        <Toggle
                            label="Active"
                            checked={isActive}
                            onChange={setIsActive}
                            description="Synced users are also blocked/unblocked in Auth0."
                        />
                    </div>

                    <div className="space-y-3">
                        <Field label="Add company">
                            <CompanyCombobox value="" onSelect={addOrReplaceCompany} />
                        </Field>
                        <p className="text-xs text-neutral-500">
                            Users can only belong to one company at a time. Choosing a company here
                            replaces the current company selection.
                        </p>

                        <div className="space-y-3">
                            {companies.length === 0 ? (
                                <div className={formCheckboxListSurfaceClassName}>
                                    <p className="text-sm text-neutral-500">
                                        Choose a company first.
                                    </p>
                                </div>
                            ) : (
                                companies.map((company) => (
                                    <div
                                        key={company.companyId}
                                        className={formCheckboxListSurfaceClassName}
                                    >
                                        <div className="mb-3">
                                            <h3 className="text-sm font-semibold text-neutral-950">
                                                {formatCompanyLabel(company)}
                                            </h3>
                                        </div>
                                        <div className="grid gap-2 md:grid-cols-2">
                                            {company.teams.map((team) => (
                                                <label
                                                    key={`${company.companyId}-${team.teamId}`}
                                                    className={cn(
                                                        'flex items-center gap-2 rounded-md p-2 text-sm',
                                                        formFieldGroupControlClassName,
                                                    )}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={teamIds.includes(team.teamId)}
                                                        onChange={() => toggleTeam(team.teamId)}
                                                    />
                                                    <span>{formatTeamLabel(team)}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 border-t pt-4">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={updateUser.isPending}>
                            {updateUser.isPending ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ children, hint, label }: { children: JSX.Element; hint?: string; label: string }) {
    return (
        <label className="block space-y-1">
            <span className="text-sm font-medium text-neutral-800">
                {label}
                {hint ? (
                    <span className="ml-2 text-xs font-normal text-neutral-500">{hint}</span>
                ) : null}
            </span>
            {children}
        </label>
    );
}

function Toggle({
    checked,
    description,
    label,
    onChange,
}: {
    checked: boolean;
    description: string;
    label: string;
    onChange: (checked: boolean) => void;
}) {
    return (
        <label className={`flex items-start gap-3 ${formFieldGroupSurfaceClassName}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                className="mt-1 size-4 rounded border-neutral-300"
            />
            <span>
                <span className="block text-sm font-medium text-neutral-950">{label}</span>
                <span className="block text-xs text-neutral-500">{description}</span>
            </span>
        </label>
    );
}

function CompanyCombobox({
    onSelect,
    value,
}: {
    onSelect: (company: AdminUserEditCompanyOption) => void;
    value: string;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const optionsQuery = useAdminUserEditOptions(query, 25, open);
    const companies = optionsQuery.data?.pages.flatMap((page) => page.items) ?? [];

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: PointerEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [open]);

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 16;
        if (nearBottom && optionsQuery.hasNextPage && !optionsQuery.isFetchingNextPage) {
            void optionsQuery.fetchNextPage();
        }
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                className="flex h-9 w-full items-center rounded-md admin-field-fill px-3 text-left text-sm text-neutral-900"
                onClick={() => setOpen((current) => !current)}
            >
                <span className={value ? 'truncate' : 'truncate text-neutral-500'}>
                    {value || 'Choose a company'}
                </span>
            </button>
            {open && (
                <div className="absolute z-50 mt-1 w-full rounded-md border border-neutral-200 bg-white p-2 shadow-lg">
                    <Input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search company name or ID"
                        autoFocus
                    />
                    <div className="mt-2 max-h-56 overflow-y-auto" onScroll={handleScroll}>
                        {companies.map((company) => (
                            <button
                                key={company.companyId}
                                type="button"
                                className="block w-full rounded px-2 py-2 text-left text-sm hover:bg-neutral-50"
                                onClick={() => {
                                    onSelect(company);
                                    setOpen(false);
                                }}
                            >
                                <span className="block font-medium text-neutral-950">
                                    {formatCompanyLabel(company)}
                                </span>
                                <span className="block text-xs text-neutral-500">
                                    {company.teams.length} teams:{' '}
                                    {company.teams.slice(0, 3).map(formatTeamLabel).join(', ')}
                                    {company.teams.length > 3 ? '…' : ''}
                                </span>
                            </button>
                        ))}
                        {optionsQuery.isFetching && (
                            <p className="px-2 py-2 text-xs text-neutral-500">Loading...</p>
                        )}
                        {!optionsQuery.isFetching && companies.length === 0 && (
                            <p className="px-2 py-2 text-xs text-neutral-500">
                                No companies found.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
